import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useInvoiceCart } from '@/hooks/useInvoiceCart';
import { useTourContext } from '@/store/tourContext';
import type { Invoice } from '@/types/invoice';
import type { TourElementKey } from '@/utils/tutorials';
import { tutorialActions } from '@/utils/tutorials';
import { generateMockInvoiceData } from '@/utils/tutorials/mockData/invoicesMockData';
import { useTutorialActive } from './useTutorialActive';

interface UseInvoicesTutorialProps {
	invoicesTableRef: React.RefObject<HTMLDivElement | null>;
	openDemoInvoiceModal?: () => void;
	openDemoDenialSupportForm?: () => void;
	openConfirmationModal?: () => void;
	selectInvoicesForTutorial?: (invoiceIds: string[]) => void;
	onSelectInvoices?: (invoiceIds: string[]) => void;
	onSetSelectedInvoiceData?: (invoices: Invoice[]) => void;
	expandedInvoicesCount?: number;
	openConfirmationModalState?: boolean;
	stripeDisclaimerRef?: React.RefObject<HTMLElement | null>;
	confirmButtonRef?: React.RefObject<HTMLButtonElement | null>;
	checkoutButtonRef?: React.RefObject<HTMLButtonElement | null>;
}

// Helper function to create a wrapper element for an entire column
const createColumnWrapper = (
	table: Element,
	columnIndex: number,
): HTMLElement | null => {
	const tableElement = table;
	if (!tableElement) return null;

	const allRows = tableElement.querySelectorAll('tr');
	if (allRows.length === 0) return null;

	const headerRow = allRows[0];
	const headerCell = headerRow.children[columnIndex] as HTMLElement;
	if (!headerCell) return null;

	// Only include data rows (skip header row)
	const dataCellsInColumn: HTMLElement[] = [];

	// Start from row 1 (skip header row at index 0)
	for (let i = 1; i < allRows.length; i++) {
		const row = allRows[i];
		const cell = row.children[columnIndex] as HTMLElement;
		if (cell) {
			// Check if cell is actually visible
			const cellRect = cell.getBoundingClientRect();
			if (cellRect.height > 0 && cellRect.width > 0) {
				dataCellsInColumn.push(cell);
			}
		}
	}

	if (dataCellsInColumn.length === 0) return null;

	const firstCellRect = dataCellsInColumn[0].getBoundingClientRect();
	const lastCellRect = dataCellsInColumn[dataCellsInColumn.length - 1].getBoundingClientRect();
	
	// Calculate height: from top of first data cell to bottom of last data cell
	// This ensures we only highlight the checkbox cells, not the header
	const totalHeight = lastCellRect.bottom - firstCellRect.top;

	const wrapper = document.createElement('div');
	wrapper.style.position = 'fixed';
	wrapper.style.left = `${firstCellRect.left}px`;
	wrapper.style.top = `${firstCellRect.top}px`;
	wrapper.style.width = `${firstCellRect.width}px`;
	wrapper.style.height = `${totalHeight}px`;
	wrapper.style.maxHeight = `${totalHeight}px`;
	wrapper.style.pointerEvents = 'none';
	wrapper.style.zIndex = '9998';
	wrapper.style.boxSizing = 'border-box';
	wrapper.style.overflow = 'hidden';
	wrapper.setAttribute('data-tour-column-wrapper', 'true');
	wrapper.setAttribute('data-column-index', String(columnIndex));

	document.body.appendChild(wrapper);

	return wrapper;
};

// Helper function to register column references
const registerColumnRefs = (
	thead: Element,
	registerElement: (key: TourElementKey, element: HTMLElement | null) => void,
) => {
	const headers = thead.querySelectorAll('th');

	headers.forEach((header) => {
		const headerText = header.textContent?.trim() || '';
		if (headerText === 'Invoice No.' || headerText === 'Invoice No') {
			registerElement('InvoicesColumnInvoiceNumberRef', header as HTMLElement);
		} else if (headerText === 'Product' || headerText.includes('Product')) {
			registerElement('InvoicesColumnProductRef', header as HTMLElement);
		} else if (
			headerText === 'Tissue ID' ||
			headerText.includes('Tissue ID') ||
			headerText.includes('TID')
		) {
			registerElement('InvoicesColumnTIDRef', header as HTMLElement);
		} else if (headerText === 'Balance' || headerText.includes('Balance')) {
			registerElement('InvoicesColumnBalanceRef', header as HTMLElement);
		} else if (
			headerText === 'Patient Name' ||
			headerText.includes('Patient')
		) {
			registerElement('InvoicesColumnPatientNameRef', header as HTMLElement);
		} else if (headerText === 'Status' || headerText.includes('Status')) {
			registerElement('InvoicesColumnStatusRef', header as HTMLElement);
		}
	});

	// Register checkbox column (first column) - entire column
	const firstHeader = headers[0];
	if (firstHeader) {
		let table: Element | null = thead.closest('table');
		if (!table) {
			table = thead.parentElement?.closest('table') || null;
		}
		if (!table) {
			let parent = thead.parentElement;
			while (parent && !table) {
				if (parent.tagName === 'TABLE') {
					table = parent;
				} else {
					parent = parent.parentElement;
				}
			}
		}

		if (table) {
			const columnIndex = 0;

			// Clean up any existing wrapper first - remove ALL existing wrappers
			const existingWrappers = document.querySelectorAll(
				'[data-tour-column-wrapper="true"]',
			);
			existingWrappers.forEach((wrapper) => {
				const el = wrapper as HTMLElement & { __cleanup?: () => void };
				if (el.__cleanup) {
					el.__cleanup();
				}
				el.remove();
			});

			const columnWrapper = createColumnWrapper(table, columnIndex);
			if (columnWrapper) {
				let rafId: number | null = null;
				
				const updateWrapperPosition = () => {
					// Cancel any pending animation frame
					if (rafId !== null) {
						cancelAnimationFrame(rafId);
					}
					
					rafId = requestAnimationFrame(() => {
						const allRows = table.querySelectorAll('tr');
						if (allRows.length === 0) return;

						// Only include data rows (skip header row)
						const dataCellsInColumn: HTMLElement[] = [];

						// Start from row 1 (skip header row at index 0)
						for (let i = 1; i < allRows.length; i++) {
							const row = allRows[i];
							const cell = row.children[columnIndex] as HTMLElement;
							if (cell) {
								// Check if cell is actually visible in viewport
								const cellRect = cell.getBoundingClientRect();
								if (cellRect.height > 0 && cellRect.width > 0) {
									dataCellsInColumn.push(cell);
								}
							}
						}

						if (dataCellsInColumn.length === 0) return;

						const firstCellRect = dataCellsInColumn[0].getBoundingClientRect();
						const lastCellRect = dataCellsInColumn[dataCellsInColumn.length - 1].getBoundingClientRect();
						
						// Calculate height: from top of first data cell to bottom of last data cell
						// This ensures we only highlight the checkbox cells, not the header
						const totalHeight = lastCellRect.bottom - firstCellRect.top;

						columnWrapper.style.left = `${firstCellRect.left}px`;
						columnWrapper.style.top = `${firstCellRect.top}px`;
						columnWrapper.style.width = `${firstCellRect.width}px`;
						// Use exact height - only data rows, no header
						columnWrapper.style.height = `${totalHeight}px`;
						columnWrapper.style.maxHeight = `${totalHeight}px`;
						columnWrapper.style.overflow = 'hidden';
					});
				};

				updateWrapperPosition();
				
				// Listen to scroll on window and any scrollable parent containers
				window.addEventListener('scroll', updateWrapperPosition, true);
				window.addEventListener('resize', updateWrapperPosition);
				
				// Also listen to scroll on the table container if it exists
				const tableContainer = table.closest('[data-id="smart-data-table"]') || 
					table.closest('.chakra-table__container') ||
					table.parentElement;
				if (tableContainer) {
					tableContainer.addEventListener('scroll', updateWrapperPosition, true);
				}

				(columnWrapper as HTMLElement & { __cleanup?: () => void }).__cleanup =
					() => {
						if (rafId !== null) {
							cancelAnimationFrame(rafId);
						}
						window.removeEventListener('scroll', updateWrapperPosition, true);
						window.removeEventListener('resize', updateWrapperPosition);
						if (tableContainer) {
							tableContainer.removeEventListener('scroll', updateWrapperPosition, true);
						}
					};

				// Register both references to the same column wrapper
				registerElement('InvoicesCheckboxColumnRef', columnWrapper);
				registerElement('InvoicesCheckboxColumnRef2', columnWrapper);
			} else {
				// Register both references to the same header
				registerElement(
					'InvoicesCheckboxColumnRef',
					firstHeader as HTMLElement,
				);
				registerElement(
					'InvoicesCheckboxColumnRef2',
					firstHeader as HTMLElement,
				);
			}
		} else {
			// Register both references to the same header
			registerElement('InvoicesCheckboxColumnRef', firstHeader as HTMLElement);
			registerElement('InvoicesCheckboxColumnRef2', firstHeader as HTMLElement);
		}
	}
};

/**
 * Custom hook to manage all Invoices tutorial logic
 * Handles tutorial actions, menu states, ref registrations, and column registrations
 */
export const useInvoicesTutorial = ({
	invoicesTableRef,
	openDemoInvoiceModal,
	openDemoDenialSupportForm,
	openConfirmationModal,
	selectInvoicesForTutorial,
	onSelectInvoices,
	onSetSelectedInvoiceData,
	expandedInvoicesCount = 0,
	openConfirmationModalState = false,
	stripeDisclaimerRef,
	confirmButtonRef,
	checkoutButtonRef,
}: UseInvoicesTutorialProps): void => {
	const { registerElement } = useTourContext();
	const { 
		isAnyInvoiceOrOnboardingTutorialActive: isAnyInvoiceTutorialActive,
	} = useTutorialActive();

	const navigate = useNavigate();
	const { addInvoiceToCart, clearInvoicesFromCart } = useInvoiceCart();
	
	// Track previous tutorial state to detect when tutorial ends
	const prevTutorialActiveRef = useRef<boolean>(false);

	// Function to select invoices so the Action menu appears and stays open
	const openActionMenuAndHighlightAddToCart = useCallback(() => {
		if (!isAnyInvoiceTutorialActive) return;

		// Generate mock data for all invoice tutorials
		const mockInvoices = generateMockInvoiceData(10);
		const invoiceIdsToSelect = mockInvoices
			.slice(0, 2)
			.map((inv) => String(inv.InvoiceID));

		if (onSelectInvoices) {
			onSelectInvoices(invoiceIdsToSelect);
		}
		if (onSetSelectedInvoiceData) {
			const selectedData = mockInvoices.slice(0, 2);
			onSetSelectedInvoiceData(selectedData);
		}

		setTimeout(() => {
			import('@/components/Molecules/ActionMenuWithTutorialSupport').then(
				(module) => {
					if (module.setForceOpenMenuForTutorial) {
						module.setForceOpenMenuForTutorial(true);
					}
				},
			);
		}, 300);
	}, [
		isAnyInvoiceTutorialActive,
		onSelectInvoices,
		onSetSelectedInvoiceData,
	]);

	// Function to open action menu for View Invoice step
	const openActionMenuForView = useCallback(() => {
		if (!isAnyInvoiceTutorialActive) return;

		const mockInvoices = generateMockInvoiceData(10);
		const invoiceIdsToSelect = mockInvoices
			.slice(0, 2)
			.map((inv) => String(inv.InvoiceID));

		if (onSelectInvoices) {
			onSelectInvoices(invoiceIdsToSelect);
		}
		if (onSetSelectedInvoiceData) {
			const selectedData = mockInvoices.slice(0, 2);
			onSetSelectedInvoiceData(selectedData);
		}

		setTimeout(() => {
			import('@/components/Molecules/ActionMenuWithTutorialSupport').then(
				(module) => {
					if (module.setForceOpenMenuForTutorial) {
						module.setForceOpenMenuForTutorial(true);
					}
				},
			);
		}, 300);
	}, [
		isAnyInvoiceTutorialActive,
		onSelectInvoices,
		onSetSelectedInvoiceData,
	]);

	// Function to open action menu for Denial Support step
	const openActionMenuForDenial = useCallback(() => {
		if (!isAnyInvoiceTutorialActive) return;

		const mockInvoices = generateMockInvoiceData(10);
		const invoiceIdsToSelect = mockInvoices
			.slice(0, 2)
			.map((inv) => String(inv.InvoiceID));

		if (onSelectInvoices) {
			onSelectInvoices(invoiceIdsToSelect);
		}
		if (onSetSelectedInvoiceData) {
			const selectedData = mockInvoices.slice(0, 2);
			onSetSelectedInvoiceData(selectedData);
		}

		setTimeout(() => {
			import('@/components/Molecules/ActionMenuWithTutorialSupport').then(
				(module) => {
					if (module.setForceOpenMenuForTutorial) {
						module.setForceOpenMenuForTutorial(true);
					}
				},
			);
		}, 300);
	}, [
		isAnyInvoiceTutorialActive,
		onSelectInvoices,
		onSetSelectedInvoiceData,
	]);

	// Generic function to open action menu (used by openActionMenu action)
	const openActionMenu = useCallback(() => {
		if (!isAnyInvoiceTutorialActive) return;

		const mockInvoices = generateMockInvoiceData(10);
		const invoiceIdsToSelect = mockInvoices
			.slice(0, 2)
			.map((inv) => String(inv.InvoiceID));

		if (onSelectInvoices) {
			onSelectInvoices(invoiceIdsToSelect);
		}
		if (onSetSelectedInvoiceData) {
			const selectedData = mockInvoices.slice(0, 2);
			onSetSelectedInvoiceData(selectedData);
		}

		setTimeout(() => {
			import('@/components/Molecules/ActionMenuWithTutorialSupport').then(
				(module) => {
					if (module.setForceOpenMenuForTutorial) {
						module.setForceOpenMenuForTutorial(true);
					}
				},
			);
		}, 300);
	}, [
		isAnyInvoiceTutorialActive,
		onSelectInvoices,
		onSetSelectedInvoiceData,
	]);

	// Register tutorial actions
	useEffect(() => {
		// Action to open demo invoice modal
		if (openDemoInvoiceModal) {
			tutorialActions.register('openViewInvoicesModal', () => {
				openDemoInvoiceModal();
			});
		}

		// Action to open demo denial support form
		if (openDemoDenialSupportForm) {
			tutorialActions.register('openDenialSupportForm', () => {
				openDemoDenialSupportForm();
			});
		}

		// Action to activate cart (add invoices to cart)
		tutorialActions.register('activateCart', () => {
			if (isAnyInvoiceTutorialActive) {
				const mockInvoices = generateMockInvoiceData(3);
				mockInvoices.forEach((invoice) => {
					addInvoiceToCart(invoice);
				});
			}
		});

		// Action to open cart (navigate to /invoice-cart and ensure invoices are there)
		tutorialActions.register('openCart', () => {
			if (isAnyInvoiceTutorialActive) {	
				clearInvoicesFromCart();
				const mockInvoices = generateMockInvoiceData(3);
				mockInvoices.forEach((invoice) => {
					addInvoiceToCart(invoice);
				});
				navigate('/invoice-cart');
			}
		});

		// Action to highlight checkout button (only navigate, don't add invoices if already in cart)
		tutorialActions.register('highlightCheckout', () => {
			if (isAnyInvoiceTutorialActive) {
				if (window.location.pathname !== '/invoice-cart') {
					navigate('/invoice-cart');
				}
			}
		});

		// Action to show Stripe disclaimer (open confirmation modal)
		tutorialActions.register('showStripeDisclaimer', () => {
			if (isAnyInvoiceTutorialActive && openConfirmationModal) {
				openConfirmationModal();
			}
		});

		// Action to highlight confirm button (ensure modal is open)
		tutorialActions.register('highlightConfirm', () => {
			if (isAnyInvoiceTutorialActive && openConfirmationModal) {
				openConfirmationModal();
			}
		});

		// Action to show Stripe disclaimer with confirm button (open confirmation modal)
		tutorialActions.register('showStripeDisclaimerWithConfirmButton', () => {
			if (isAnyInvoiceTutorialActive) {
				if (window.location.pathname !== '/invoice-cart') {
					navigate('/invoice-cart?openStripeModal=true');
				} else {
					if (openConfirmationModal) {
						openConfirmationModal();
					}
				}
			}
		});

		tutorialActions.register('highlightNextColumn', () => {
			// This is handled automatically by the tutorial system
		});

		// Action to open action button and highlight Add to Cart
		tutorialActions.register('openActionButtonToAdd', () => {
			if (isAnyInvoiceTutorialActive) {
				openActionMenuAndHighlightAddToCart();
			}
		});

		// Action to open action menu (generic - used before showing View/Denial Support)
		tutorialActions.register('openActionMenu', () => {
			if (isAnyInvoiceTutorialActive) {
				openActionMenu();
			}
		});

		// Action to expand action dropdown for view
		tutorialActions.register('expandActionDropdownView', () => {
			if (isAnyInvoiceTutorialActive) {
				openActionMenuForView();
			}
		});

		// Action to expand action dropdown for denial
		tutorialActions.register('expandActionDropdownDenial', () => {
			if (isAnyInvoiceTutorialActive) {
				openActionMenuForDenial();
			}
		});

		// Action to remove invoices from cart (after confirming payment lock)
		tutorialActions.register('removeInvoicesOfCart', () => {
			if (isAnyInvoiceTutorialActive) {
				clearInvoicesFromCart();
			}
		});

		// Action to clean shopping cart (clean all mocked data when leaving checkout page)
		tutorialActions.register('cleanShoppingCart', () => {
			if (isAnyInvoiceTutorialActive) {
				// Clear cart and selected invoices
				clearInvoicesFromCart();
				// Clear table selections if callbacks are available
				if (onSelectInvoices) {
					onSelectInvoices([]);
				}
				if (onSetSelectedInvoiceData) {
					onSetSelectedInvoiceData([]);
				}
			}
		});

		return () => {
			if (openDemoInvoiceModal) {
				tutorialActions.unregister('openViewInvoicesModal');
			}
			if (openDemoDenialSupportForm) {
				tutorialActions.unregister('openDenialSupportForm');
			}
			tutorialActions.unregister('activateCart');
			tutorialActions.unregister('openCart');
			tutorialActions.unregister('highlightCheckout');
			tutorialActions.unregister('showStripeDisclaimer');
			tutorialActions.unregister('highlightConfirm');
			tutorialActions.unregister('highlightNextColumn');
			tutorialActions.unregister('openActionButtonToAdd');
			tutorialActions.unregister('openActionMenu');
			tutorialActions.unregister('expandActionDropdownView');
			tutorialActions.unregister('expandActionDropdownDenial');
			tutorialActions.unregister('removeInvoicesOfCart');
			tutorialActions.unregister('cleanShoppingCart');
		};
	}, [
		openDemoInvoiceModal,
		openDemoDenialSupportForm,
		openConfirmationModal,
		selectInvoicesForTutorial,
		openActionMenuAndHighlightAddToCart,
		openActionMenu,
		openActionMenuForView,
		openActionMenuForDenial,
		navigate,
		addInvoiceToCart,
		clearInvoicesFromCart,
		isAnyInvoiceTutorialActive,
	]);

	// Register column refs - register immediately when tutorial is active and table is mounted
	useEffect(() => {
		// Only register if tutorial is active
		if (!isAnyInvoiceTutorialActive) {
			return;
		}

		const registerColumns = () => {
			if (!invoicesTableRef.current) {
				// Clean up column wrapper if it exists
				const existingWrappers = document.querySelectorAll(
					'[data-tour-column-wrapper="true"]',
				);
				existingWrappers.forEach((wrapper) => {
					const el = wrapper as HTMLElement & { __cleanup?: () => void };
					if (el.__cleanup) {
						el.__cleanup();
					}
					el.remove();
				});

				registerElement('InvoicesColumnInvoiceNumberRef', null);
				registerElement('InvoicesColumnProductRef', null);
				registerElement('InvoicesColumnTIDRef', null);
				registerElement('InvoicesColumnBalanceRef', null);
				registerElement('InvoicesColumnPatientNameRef', null);
				registerElement('InvoicesColumnStatusRef', null);
				registerElement('InvoicesCheckboxColumnRef', null);
				registerElement('InvoicesCheckboxColumnRef2', null);
				return false; // Table not ready
			}

			const tableRoot = invoicesTableRef.current;
			if (!tableRoot) return false;

			const thead = tableRoot.querySelector('thead');
			if (!thead) {
				return false; // Table not ready yet
			}

			// Check if table has rows (data is loaded)
			const tbody = tableRoot.querySelector('tbody');
			if (!tbody || tbody.querySelectorAll('tr').length === 0) {
				return false; // Data not loaded yet
			}

			registerColumnRefs(thead, registerElement);
			return true; // Successfully registered
		};

		// Try to register immediately
		let registered = registerColumns();
		
		// If not registered, try with intervals until successful or tutorial ends
		if (!registered) {
			const intervalId = setInterval(() => {
				if (!isAnyInvoiceTutorialActive) {
					clearInterval(intervalId);
					return;
				}
				registered = registerColumns();
				if (registered) {
					clearInterval(intervalId);
				}
			}, 200); // Check every 200ms

			// Also try with timeouts as fallback
			const timeoutId1 = setTimeout(() => {
				if (isAnyInvoiceTutorialActive) registerColumns();
			}, 500);
			const timeoutId2 = setTimeout(() => {
				if (isAnyInvoiceTutorialActive) registerColumns();
			}, 1000);
			const timeoutId3 = setTimeout(() => {
				if (isAnyInvoiceTutorialActive) registerColumns();
			}, 2000);

			return () => {
				clearInterval(intervalId);
				clearTimeout(timeoutId1);
				clearTimeout(timeoutId2);
				clearTimeout(timeoutId3);
			};
		}

		// Cleanup function when tutorial ends or component unmounts
		return () => {
			if (!isAnyInvoiceTutorialActive) {
				const existingWrappers = document.querySelectorAll(
					'[data-tour-column-wrapper="true"]',
				);
				existingWrappers.forEach((wrapper) => {
					const el = wrapper as HTMLElement & { __cleanup?: () => void };
					if (el.__cleanup) {
						el.__cleanup();
					}
					el.remove();
				});
			}
		};
	}, [invoicesTableRef, registerElement, isAnyInvoiceTutorialActive]);

	// Register trash icon refs from cart items - always available, continuously check
	useEffect(() => {
		const registerTrashIcon = () => {
			const trashIcons = document.querySelectorAll(
				'[aria-label="Delete Invoice"]',
			);
			if (trashIcons.length > 0) {
				registerElement(
					'InvoicesCartTrashIconRef',
					trashIcons[0] as HTMLElement,
				);
			} else {
				registerElement('InvoicesCartTrashIconRef', null);
			}
		};

		// Try immediately and then periodically
		registerTrashIcon();
		const timeoutId1 = setTimeout(registerTrashIcon, 500);
		const timeoutId2 = setTimeout(registerTrashIcon, 1000);
		const intervalId = setInterval(registerTrashIcon, 2000);

		return () => {
			clearTimeout(timeoutId1);
			clearTimeout(timeoutId2);
			clearInterval(intervalId);
		};
	}, [expandedInvoicesCount, registerElement]);

	// Register checkout button ref - always available, continuously check
	useEffect(() => {
		const registerCheckoutButton = () => {
			let foundButton: HTMLElement | null = null;
			
			if (checkoutButtonRef?.current) {
				const btn = checkoutButtonRef.current;
				const style = window.getComputedStyle(btn);
				const rect = btn.getBoundingClientRect();
				const isVisible = 
					style.display !== 'none' &&
					style.visibility !== 'hidden' &&
					rect.width > 0 &&
					rect.height > 0 &&
					btn.offsetHeight > 0 &&
					btn.offsetWidth > 0;
				
				if (isVisible) {
					foundButton = btn;
				}
			}
			
						if (!foundButton) {
				const buttons = document.querySelectorAll('button');
				const checkoutButton = Array.from(buttons).find((btn) => {
					const text = btn.textContent?.trim() || '';
					const normalizedText = text.toLowerCase().replace(/\s+/g, ' ');
					
					// Check for exact match or normalized match (handles extra spaces, case variations)
					const isCheckoutButton = 
						text === 'Proceed to Checkout' ||
						normalizedText === 'proceed to checkout';
					
					if (!isCheckoutButton) {
						return false;
					}
					
					// Verify button is visible
					const style = window.getComputedStyle(btn);
					const rect = btn.getBoundingClientRect();
					const isVisible = 
						style.display !== 'none' &&
						style.visibility !== 'hidden' &&
						rect.width > 0 &&
						rect.height > 0 &&
						btn.offsetHeight > 0 &&
						btn.offsetWidth > 0;
					
					return isVisible;
				});
				
				if (checkoutButton) {
					foundButton = checkoutButton as HTMLElement;
				}
			}
			
			// Register the found button (or null if not found)
			if (foundButton) {
				registerElement(
					'InvoicesCartCheckoutButtonRef',
					foundButton,
				);
			} else {
				registerElement('InvoicesCartCheckoutButtonRef', null);
			}
		};

		// Try immediately and then periodically
		registerCheckoutButton();
		const timeoutId1 = setTimeout(registerCheckoutButton, 500);
		const timeoutId2 = setTimeout(registerCheckoutButton, 1000);
		const timeoutId3 = setTimeout(registerCheckoutButton, 1500);
		const timeoutId4 = setTimeout(registerCheckoutButton, 2000);
		const intervalId = setInterval(registerCheckoutButton, 2000);

		return () => {
			clearTimeout(timeoutId1);
			clearTimeout(timeoutId2);
			clearTimeout(timeoutId3);
			clearTimeout(timeoutId4);
			clearInterval(intervalId);
		};
	}, [expandedInvoicesCount, registerElement, checkoutButtonRef]);

	// Register stripe disclaimer and confirm button refs when modal opens - always available
	useEffect(() => {
		if (openConfirmationModalState) {
			const timeoutId = setTimeout(() => {
				// Use ref if available, otherwise try to find by text
				if (stripeDisclaimerRef?.current) {
					registerElement(
						'InvoicesStripeDisclaimerRef',
						stripeDisclaimerRef.current,
					);
				} else {
					// Fallback: Try to find stripe disclaimer by text
					const allElements = document.querySelectorAll('*');
					let stripeDisclaimer: HTMLElement | null = null;
					for (const el of Array.from(allElements)) {
						const text = el.textContent || '';
						if (
							text.includes('Stripe') &&
							text.includes('disclaimer') &&
							(el.tagName === 'P' ||
								el.tagName === 'DIV' ||
								el.tagName === 'SPAN')
						) {
							stripeDisclaimer = el as HTMLElement;
							break;
						}
					}
					if (stripeDisclaimer) {
						registerElement('InvoicesStripeDisclaimerRef', stripeDisclaimer);
					} else {
						registerElement('InvoicesStripeDisclaimerRef', null);
					}
				}

				// Use ref if available, otherwise try to find by text
				if (confirmButtonRef?.current) {
					registerElement('InvoicesConfirmButtonRef', confirmButtonRef.current);
				} else {
					// Fallback: Try to find confirm button by text
					const buttons = document.querySelectorAll('button');
					const confirmButton = Array.from(buttons).find(
						(btn) => btn.textContent?.trim() === 'CONFIRM',
					);
					if (confirmButton) {
						registerElement(
							'InvoicesConfirmButtonRef',
							confirmButton as HTMLElement,
						);
					} else {
						registerElement('InvoicesConfirmButtonRef', null);
					}
				}
			}, 300);

			return () => clearTimeout(timeoutId);
		} else {
			registerElement('InvoicesStripeDisclaimerRef', null);
			registerElement('InvoicesConfirmButtonRef', null);
		}
	}, [
		openConfirmationModalState,
		registerElement,
		stripeDisclaimerRef,
		confirmButtonRef,
	]);

	// Cleanup all invoice-related data when tutorial ends
	// This ensures everything is cleaned up when the tutorial completes or is skipped
	useEffect(() => {
		// Only cleanup when tutorial transitions from active to inactive
		const wasActive = prevTutorialActiveRef.current;
		const isActive = isAnyInvoiceTutorialActive;
		
		// Update ref for next comparison
		prevTutorialActiveRef.current = isActive;
		
		// Only cleanup when transitioning from active to inactive
		if (wasActive && !isActive) {
			// Clear cart
			clearInvoicesFromCart();
			// Clear table selections if callbacks are available
			if (onSelectInvoices) {
				onSelectInvoices([]);
			}
			if (onSetSelectedInvoiceData) {
				onSetSelectedInvoiceData([]);
			}
		}
	}, [isAnyInvoiceTutorialActive, clearInvoicesFromCart, onSelectInvoices, onSetSelectedInvoiceData]);

	// All tutorial logic is handled internally via effects and action registrations
};
